import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, Copy } from "lucide-react";
import { toast } from "sonner";
import Chapter from "./Chapter";
import { EMAIL, LOGO_CLARO, SOCIALS } from "../data/films";

const EASE = [0.22, 1, 0.36, 1];

export default function Footer() {
    const headlineRef = useRef(null);
    const showHeadline = useInView(headlineRef, { once: true, margin: "-60px" });

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(EMAIL);
            toast.success("Correo copiado al portapapeles");
        } catch {
            toast.error("No se pudo copiar el correo");
        }
    };

    return (
        <footer id="contacto" className="bg-[#141312] text-[#FAF8F5]" data-testid="footer-section">
            <div className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 sm:py-32">
                <div className="mb-14 sm:mb-20">
                    <p className="font-mono-jf text-xs uppercase tracking-[0.25em] text-[#C98993]" data-testid="chapter-04-eyebrow">
                        04 / Conexión directa
                    </p>
                    <div className="mt-6 h-px w-full bg-[#FAF8F5]/10" />
                </div>

                <div ref={headlineRef} className="overflow-hidden">
                    <motion.h2
                        animate={showHeadline ? { y: "0%" } : { y: "110%" }}
                        transition={{ duration: 0.9, ease: EASE }}
                        className="font-display text-4xl leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl"
                        data-testid="footer-headline"
                    >
                        ¿Hablamos <em className="italic text-[#C98993]">de cine?</em>
                    </motion.h2>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
                    className="mt-14"
                >
                    <div className="group inline-flex flex-wrap items-center gap-4">
                        <a
                            href={`mailto:${EMAIL}`}
                            className="font-display text-2xl tracking-tight underline decoration-[#C98993]/50 underline-offset-8 transition-colors duration-300 hover:text-[#C98993] sm:text-4xl"
                            data-testid="footer-email-link"
                        >
                            {EMAIL}
                        </a>
                        <button
                            onClick={copyEmail}
                            className="rounded-full border border-[#FAF8F5]/20 p-2.5 transition-colors duration-300 hover:border-[#C98993] hover:text-[#C98993]"
                            aria-label="Copiar correo"
                            data-testid="footer-email-copy"
                        >
                            <Copy size={15} />
                        </button>
                    </div>

                    <div className="mt-16 grid grid-cols-2 gap-px bg-[#FAF8F5]/10 sm:grid-cols-4">
                        {SOCIALS.map((s) => (
                            <a
                                key={s.label}
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group flex items-center justify-between bg-[#141312] px-5 py-6 font-mono-jf text-xs uppercase tracking-[0.2em] text-[#FAF8F5]/70 transition-colors duration-300 hover:text-[#C98993]"
                                data-testid={`social-${s.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                            >
                                {s.label}
                                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                            </a>
                        ))}
                    </div>
                </motion.div>

                <div className="mt-20 flex flex-col items-start justify-between gap-6 border-t border-[#FAF8F5]/10 pt-8 sm:flex-row sm:items-center">
                    {/* Logotipo en tinta clara, integrado al fondo oscuro */}
                    <img
                        src={LOGO_CLARO}
                        alt="Jaqueca Films — logotipo ilustrado"
                        className="h-28 w-auto opacity-90"
                        data-testid="footer-logo"
                    />
                    <p className="font-mono-jf text-[10px] uppercase tracking-[0.25em] text-[#FAF8F5]/40" data-testid="footer-copyright">
                        © {new Date().getFullYear()} Jaqueca Films · México · Todo por la ficción
                    </p>
                </div>
            </div>
        </footer>
    );
}
